import type { Messages } from '../messages.js'

/**
 * Welsh. `symudiad` for flips, the word a Welsh board game uses for a move.
 *
 * Welsh has six CLDR plural forms, more than anything else here: separate ones for one, two,
 * three and six, which are genuinely different words rather than a technicality — `dau air`,
 * `tri gair`, `chwe gair`. The initial consonant mutates after some of them, which is why
 * `gair` appears as `air` after `dau`.
 */
export const cy: Messages = {
  tag: 'cy',

  readingDictionary: 'Yn darllen y geiriadur…',
  noWordList: 'Dim rhestr eiriau ar gyfer "{language}". Lluniwch un:  pnpm dictionary build',
  emptyWordList: 'Mae rhestr eiriau "{language}" yn wag.',

  flips: 'symudiadau',
  score: 'sgôr',
  words: 'geiriau',
  round: 'rownd',
  ticksLeftLabel: 'Amser sydd ar ôl yn y rownd hon',
  typeAWord: 'teipiwch air',
  tapPrompt: 'tapiwch lythrennau i’w dewis neu eu dychwelyd, yna {action}',

  boardOfTiles: 'Bwrdd o {n} teilsen',
  faceDown: 'wyneb i waered',
  wildCard: 'cerdyn gwyllt',
  wildKey: 'unrhyw lythyren',
  letterReplaced: 'daeth {from} yn {to}',
  letterSwap: 'CYFNEWID LLYTHRENNAU!',
  spentTile: 'teilsen wedi’i defnyddio',
  hiddenWhilePaused: 'wedi’i guddio tra bo saib',
  letterInWord: '{letter}, llythyren {position} y gair',

  completeWord: 'Cwblhau’r gair',

  completeShort: 'Cwblhau',
  reset: 'Clirio',
  pause: 'Saib',
  resume: 'Ailddechrau',
  newGame: 'Gêm newydd',
  paused: 'Ar saib',
  outOfFlips: 'Dim symudiadau ar ôl',
  finalResult: '{score} am {words} dros {rounds}',
  playAgain: 'Chwarae eto',
  share: 'Rhannu',
  shareCopied: 'Wedi copïo.',
  shareSelect: 'Copïwch hyn:',

  lettersSelect: 'llythrennau’n dewis',
  keysWild: 'fe’i cymerir pan deipiwch lythyren nad oes teilsen yn ei dangos',
  clearsEvery: 'yn clirio pob {letter} a ddewiswyd',
  undoLastLetter: 'dadwneud y llythyren olaf',
  noWordsYet: 'Dim geiriau eto.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'wedi’i ganfod eisoes',
  reasonTooShort: 'rhy fyr',
  reasonNotAWord: 'nid yw’n air',
  reasonAllFound: 'mae gennych bob un ohonynt',
  noSuchLetterUp: 'dim {letter} yn y golwg',
  nothingUp: 'dim yn y golwg',
  letterHidden: 'cuddiodd llythyren',
  shuffled: 'wedi’i gymysgu',
  shuffledAndBilled: 'wedi’i gymysgu, codwyd {flips} heb eu defnyddio',
  tooFewLetters: 'Rhy ychydig o lythrennau ar ôl',

  gameLanguage: 'iaith',
  interfaceLanguage: 'rhyngwyneb',
  dictionarySize: '{common} cyffredin o {full} gair',
  filterLanguages: 'Chwilio am iaith',
  noMatches: 'Dim canlyniadau',

  nerdMode: 'modd y selogion',
  rules: 'Rheolau',
  difficulty: 'anhawster',
  difficultyNames: { easy: 'hawdd', medium: 'canolig', hard: 'anodd', insane: 'gwallgof' },
  pausedNoBoard: 'Ni fydd y gêm hon yn ymddangos ar y bwrdd arwain.',
  about: 'Ynghylch',
  themeLabel: 'thema',
  themeNames: { traditional: 'traddodiadol', light: 'golau', contrast: 'cyferbyniad uchel' },
  tiles: 'teils (N)',
  secondsPerTick: 'eiliadau / tic',
  holdTicks: 'ticiau dal',
  minWord: 'gair byrraf',
  startingFlips: 'symudiadau cychwynnol',
  wildChance: 'siawns cerdyn gwyllt',
  replaceChance: 'siawns cyfnewid llythyren',
  hideChance: 'siawns cuddio llythyren',
  wordCompleteMode: 'wrth gwblhau gair',
  wordCompleteNames: { shuffle: 'cymysgu', spend: 'gwario', keep: 'cadw' },
  flipEconomy: 'dychwelyd symudiadau',
  flipEconomyNames: {
    none: 'dim',
    perLetter: 'fesul llythyren',
    fibonacci: 'fibonacci',
    overMinimum: 'dros y lleiafswm',
  },
  repeatedLetterKey: 'bysell llythyren ailadroddus',
  keySchemeNames: { cycle: 'cylch', advance: 'ymlaen' },
  keySchemeHelp: {
    cycle:
      'Mae A yn cymryd yr A nesaf sydd heb ei defnyddio, a phan fyddant i gyd yn y gair, ' +
      'mae’n eu clirio. Mae Shift+A yn eu clirio hefyd.',
    advance:
      'Mae A yn cymryd yr A nesaf sydd heb ei defnyddio. Mae Shift+A yn clirio pob A o’r gair.',
  },

  whatThatMeans: 'Beth mae hynny’n ei olygu',
  factRound: 'rownd',
  factWholeBoardUp: 'y bwrdd cyfan yn y golwg am',
  factRoundCosts: 'mae rownd yn costio',
  factFlipsBuy: 'mae’r symudiadau cychwynnol yn prynu',
  factThisBoard: 'y bwrdd hwn',
  factBoardHadToAdmit: 'roedd rhaid i’r bwrdd dderbyn',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, yr hiraf {longest}',
  wordsIncludingOneOf: '{words}, gan gynnwys un o {ceiling}',
  scorelessRounds: '{rounds} heb sgôr',

  whatAWordPays: 'Beth mae gair yn ei dalu',
  columnLetters: 'llythrennau',
  columnCost: 'cost',
  columnPoints: 'pwyntiau',
  columnFlips: 'symudiadau',
  columnNet: 'net',

  canonicalRules: 'Rheolau arferol {difficulty}.',
  customRules: 'Wedi newid o’r rhagosodiad. Ni chaiff sgorau dan reolau eich hun eu rhestru.',
  applyAndStart: 'Gosod a dechrau gêm newydd',
  changesNextGame: 'Daw’r newidiadau i rym yn y gêm nesaf.',
  presets: 'Rhagosodiadau:',

  start: 'Dechrau',
  restart: 'Ailddechrau',
  quit: 'Gadael',
  quitTitle: 'Gadael y gêm hon?',
  restartTitle: 'Ailddechrau’r gêm hon?',
  restartConfirm: 'Ailddechrau',
  quitConfirm: 'Gadael',
  keepPlaying: 'Dal ati i chwarae',
  personalBest: 'Eich gemau gorau',
  newPersonalBest: 'Record bersonol newydd.',
  columnRank: '#',
  notRanked: 'Rheolau eich hun, felly ni chaiff y gêm hon ei rhestru.',
  notRankedPaused: 'Fe stopiodd y cloc, felly ni chaiff y gêm hon ei rhestru.',
  rankOfTotal: '{rank} o {total}',

  howToPlay: 'Sut i chwarae',

  backToGame: 'Yn ôl at y gêm',
  welcomeTitle: 'Croeso i Blinkered',
  tutorialSkip: 'Hepgor',
  tutorialNext: 'Nesaf',
  tutorialBack: 'Yn ôl',
  tutorialStart: 'Dechrau chwarae',
  tutorialHideAgain: 'Peidiwch â dangos hyn eto',
  tutorialProgress: '{n} o {total}',
  tutorialSkipTitle: 'Hepgor y cyflwyniad?',
  tutGoalTitle: 'Ras yw Blinkered',
  tutGoalBody:
    'Rydych yn dechrau’r gêm gyda nifer penodol o symudiadau, ac mae pob teilsen sy’n troi yn costio un symudiad. Pan fydd y symudiadau’n dod i ben, mae’r gêm ar ben. Mae geiriau’n ennill symudiadau, a pho hiraf y gair, y mwyaf o symudiadau a gewch.',
  tutSpentBody:
    'Mae’r llythrennau a ddefnyddiwch mewn gair yn troi wyneb i lawr tan y rownd nesaf. Dim ond newid llythyren sy’n newid pa lythrennau sydd ar y bwrdd.',
  tutChangeTitle: 'Gall llythrennau newid',
  tutSameLetters:
    'Wedi gweld gair na chawsoch gyfle i’w chwarae? Mae’r un llythrennau’n dod yn ôl y rownd nesaf, wedi’u cymysgu i leoedd newydd.',
  tutPickLetters: 'Tapiwch y llythrennau rydych eu heisiau, yn eu trefn, i ffurfio gair.',
  tutMoreTurn:
    'Mae’r teils yn dal i droi tra byddwch yn meddwl, felly gall llythyren well fod ar ei ffordd.',
  tutTapBack:
    'Wedi cymryd un nad oeddech ei heisiau? Tapiwch hi eto i’w dychwelyd. Unrhyw un, nid yr olaf yn unig.',
  tutComplete: 'Pwyswch Cwblhau pan fydd y gair yn barod.',
  htBoardTitle: 'Y bwrdd',
  htBoardBody:
    'Mae’r teils yn troi fesul un, mewn trefn ar hap. Gellir defnyddio’r rhai sydd yn y golwg i ffurfio geiriau.',
  htWordsTitle: 'Y geiriau',
  htWordsBody:
    'Ffurfiwch air o’r teils sydd yn y golwg drwy deipio neu glicio’r llythrennau yn eu trefn.',
  htFlipsTitle: 'Y symudiadau',
  htFlipsBody:
    'Mae pob teilsen sy’n troi yn costio symudiad. Mae gair cyflawn yn dychwelyd symudiadau, ac mae geiriau hirach yn talu mwy. Pan fydd y symudiadau’n dod i ben, mae’r gêm ar ben.',
  htRoundTitle: 'Y rownd',
  htRoundBody:
    'Pan fydd teilsen olaf y rownd yn troi, mae’r bwrdd cyfan yn aros am eiliad. Yna caiff y teils eu troi drosodd a’u cymysgu, ac mae rownd newydd yn dechrau.',
  htLanguagesTitle: 'Yr ieithoedd',
  htLanguagesBody:
    'Mae {n} ohonynt. Gellir datrys pob bwrdd â geiriau y mae pobl yn eu defnyddio go iawn. Mae gair anghyffredin yn sgorio hefyd, os yw’r geiriadur yn ei adnabod.',
  htKeysTitle: 'Y bysellfwrdd',
  htWildTitle: 'Cardiau gwyllt',
  htWildBody:
    'Weithiau daw cerdyn gwyllt yn lle llythyren. Mae cerdyn gwyllt yn cyfrif fel unrhyw lythyren sy’n gwneud gair dilys. Nid yw gair a gwblhawyd eisoes yn cyfrif.',
  htHideTitle: 'Llythrennau’n cuddio',
  htHideBody:
    'Ar y lefelau anoddach gall llythyren sydd yn y golwg droi’n ôl ohoni ei hun a dod yn ôl yn nes ymlaen yn y rownd. Mae’r amserydd yn cael tic ychwanegol am hynny, mewn coch. Nid yw llythrennau sydd wedi’u dewis yn troi’n ôl.',
  tutHideBody:
    'Ar y lefelau anoddach gall llythyren sydd yn y golwg droi’n ôl ohoni ei hun a dod yn ôl yn nes ymlaen yn y rownd. Nid yw llythrennau sydd wedi’u dewis yn troi’n ôl.',
  htSwapTitle: 'Llythrennau’n newid',
  htSwapBody:
    'Weithiau, rhwng rowndiau, caiff un llythyren ei disodli gan un arall. Cewch weld pa un a aeth a pha un a ddaeth.',
  htLevelsTitle: 'Y lefelau',
  htLevelEasy:
    'Yr un deuddeg llythyren drwy’r gêm, felly gallwch eu dysgu a chario rhestr o eiriau yn eich pen. Mae’r teils yn troi’n araf, ac mae’r bwrdd cyfan yn aros yn y golwg yn ddigon hir i orffen eich dewis.',
  htLevelMedium:
    'Mae llythyren yn newid nawr ac yn y man, felly mae’n anos cofio geiriau a gadwyd at nes ymlaen. Llai o amser i edrych, a llai i feddwl.',
  htLevelHard:
    'Nid yw geiriau tair llythyren yn cyfrif mwyach, ac mae llythyren yn newid bob yn ail rownd, fwy neu lai. Prin y mae’r bwrdd wedi ymddangos cyn iddo gymysgu.',
  htLevelInsane:
    'Popeth ar unwaith, ar garlam. Mae’r bwrdd yn cymysgu bron yn syth ar ôl y symudiad olaf.',
  htTouchTitle: 'Y sgrin gyffwrdd',
  htTouchBody:
    'Tapiwch deilsen sydd yn y golwg i gymryd ei llythyren. Tapiwch lythyren a gymerwyd i’w dychwelyd. Mae Cwblhau a Clirio o dan y bwrdd.',

  accountTitle: 'Dy gyfrif',
  menuProfile: 'Fy mhroffil',
  menuGames: 'Fy ngemau',
  menuPublicPage: 'Fy nhudalen gyhoeddus',
  menuSignedInAs: 'Wedi mewngofnodi fel',
  signIn: 'Mewngofnodi',
  signOut: 'Allgofnodi',
  signInTitle: 'Mewngofnoda neu gofrestra',
  signInLead: 'Mae cyfrif yn cadw dy gemau, ar bob dyfais.',
  signInKeepGame: "Mewngofnoda i gadw'r gêm hon, a phob un ar ei hôl.",
  continueWithApple: 'Parhau gydag Apple',
  continueWithGoogle: 'Parhau gyda Google',
  signInOr: 'neu',
  emailLabel: 'E-bost',
  codeLabel: 'Cod',
  emailMeACode: 'Anfonwch god ataf',
  working: 'Eiliad…',
  sendNewCode: 'Anfon cod newydd',
  codeSent: "Edrycha ar dy e-bost. Mae'r cod yn para 10 munud.",
  notNow: 'Nid nawr',
  badEmail: "Nid yw hwnna'n edrych fel cyfeiriad e-bost.",
  badCode: "Ni weithiodd y cod hwnnw. Mae codau'n darfod ar ôl 10 munud ac yn gweithio unwaith.",
  serverBusy: 'Methu cyrraedd y gweinydd. Rho gynnig arall ymhen ennyd.',
  serverDown: 'Methu cyrraedd y gweinydd.',
  offline: 'All-lein',
  leaderboardTitle: 'Y Bwrdd Arwain',
  leaderboardEmpty: 'Dim gemau ar y bwrdd hwn eto. Bydd y cyntaf.',
  leaderboardWouldBe: "Lle byddai'r gêm hon",
  leaderboardThisGame: 'Y gêm hon',
  leaderboardRanksHere: "Lle mae'r gêm hon",
  leaderboardOutside: "Y tu allan i'r pump uchaf.",
  tutAccountTitle: 'Cadw dy sgoriau',
  tutAccountBody:
    'Mae cyfrif yn cadw dy gemau, yn rhoi dy sgoriau ar y bwrdd arwain, ac yn cadw dy broffil.',
  ssoCancelled: 'Cafodd y mewngofnodi ei ganslo.',
  ssoExpired: "Cymerodd hynny'n rhy hir. Mewngofnoda eto.",
  ssoNoUsername: "Methu gorffen creu'r cyfrif. Rho gynnig arall ymhen ennyd.",
  ssoFailed: 'Ni weithiodd. Rho gynnig arall, neu defnyddia dy e-bost.',
  tabProfile: 'Proffil',
  countryAny: 'Gwell gen i beidio dweud',
  findCountry: 'Chwilio gwledydd',
  usernameLabel: 'Enw defnyddiwr',
  bioLabel: 'Amdanaf',
  countryLabel: 'Gwlad',
  bioHint: 'Dim dolenni. {left}/{max}',
  save: 'Cadw',
  saving: "Wrthi'n cadw…",
  saved: 'Wedi cadw.',
  nameTaken: "Mae rhywun eisoes â'r enw yna.",
  nameTooShort: "Ychydig yn hirach, os gweli'n dda.",
  nameTooLong: "Mae hynny'n rhy hir.",
  nameBadCharacters: 'Llythrennau, rhifau, cysylltnodau a thanlinellau yn unig.',
  nameBadEdges: 'Rhaid iddo ddechrau a gorffen â llythyren neu rif.',
  nameMixedScripts: 'Dewisa un wyddor ac aros gyda hi.',
  nameReserved: "Mae hwn yn edrych fel enw rydyn ni'n ei roi. Dewisa un arall.",
  nameUnusable: "Ni ellir defnyddio'r enw yna.",
  bioTooLong: 'Mae lle i {max} nod ar y mwyaf.',
  bioHasLink: 'Ni chaniateir dolenni yma.',
  bioHasControl: 'Mae ynddo nodau na all fod yma.',
  bioUnusable: "Ni ellir defnyddio'r testun yna.",
  gamesHeading: 'Gemau',
  gamesEmpty: "Dim byd yma eto. Bydd gemau rwyt ti'n eu chwarae wedi mewngofnodi'n ymddangos yma.",
  gamesEmptyShort: 'Dim byd yma eto.',
  gamesLoading: 'Yn darllen dy gemau…',
  theirGamesLoading: 'Yn darllen eu gemau…',
  profileLoading: 'Yn darllen y proffil…',
  playerNotFound: "Does neb yma o'r enw hwnnw.",
  columnWhen: 'Pryd',
  columnRounds: 'rownd',
  columnGame: 'Gêm',
  gameLoading: 'Yn darllen y gêm…',
  gameUnreadable: 'Methu darllen y gêm hon.',
  gameNotRanked: "Chwaraewyd â rheolau wedi'u newid, felly nid yw'n cael ei rhestru.",
  detailNotKept: "Nid yw geiriau a byrddau'r gêm hon yn cael eu cadw mwyach.",
  openingBoard: 'Y bwrdd agoriadol',
  roundByRound: 'Rownd wrth rownd',
  roundNothing: "dim byd wedi'i ganfod",
  changeReplaced: "{letter} wedi'i newid yn lle {slot}",
  changeWildOn: '{letter} jocer yn lle {slot}',
  changeWildOff: '{letter} jocer wedi mynd o le {slot}',
  keepThisGame: "Cadw'r gêm hon",
  savedToYourGames: "Wedi'i chadw i dy gemau.",

  reportThis: 'Adrodd',
  reportHeading: 'Adrodd hyn',
  reportTheName: 'Yr enw',
  reportTheBio: 'Y disgrifiad',
  reportTheScore: 'Y sgôr',
  reportWhy: 'Beth sy’n bod arno? Dewisol.',
  reportSend: 'Anfon yr adroddiad',
  reportSent: 'Diolch. Bydd rhywun yn edrych arno.',
  reportSignIn: 'Mewngofnoda i adrodd rhywbeth.',
  reportGone: 'Nid yw hwnnw yno mwyach.',
  reportCancel: 'Canslo',

  deleteAccount: 'Dileu fy nghyfrif',
  deleteAccountWhat:
    'Mae hyn yn tynnu dy broffil a phob gêm a gedwaist, am byth. Does dim modd ei ddadwneud.',
  deleteAccountAsk: 'Anfona god i mi',
  deleteConfirm: 'Dileu am byth',
  deleteGone: 'Mae dy gyfrif wedi mynd. Diolch am chwarae.',
  deleteNoAddress:
    'Does dim cyfeiriad wedi’i gadarnhau ar y cyfrif hwn, felly does dim modd gwirio’r dileu. Cysyllta â ni ac fe wnawn ni hynny â llaw.',

  rulesetFacts: 'Geiriau o {letters} · teilsen newydd bob {seconds}',
  plurals: {
    words: {
      zero: '{n} gair',
      one: '{n} gair',
      two: '{n} air',
      few: '{n} gair',
      many: '{n} gair',
      other: '{n} gair',
    },
    rounds: {
      zero: '{n} rownd',
      one: '{n} rownd',
      two: '{n} rownd',
      few: '{n} rownd',
      many: '{n} rownd',
      other: '{n} rownd',
    },
    flips: {
      zero: '{n} symudiad',
      one: '{n} symudiad',
      two: '{n} symudiad',
      few: '{n} symudiad',
      many: '{n} symudiad',
      other: '{n} symudiad',
    },
    ticks: {
      zero: '{n} tic',
      one: '{n} tic',
      two: '{n} dic',
      few: '{n} tic',
      many: '{n} tic',
      other: '{n} tic',
    },
    points: {
      zero: '{n} pwynt',
      one: '{n} pwynt',
      two: '{n} bwynt',
      few: '{n} phwynt',
      many: '{n} phwynt',
      other: '{n} pwynt',
    },
    minLetters: {
      zero: '{n} llythyren neu fwy',
      one: '{n} llythyren neu fwy',
      two: '{n} lythyren neu fwy',
      few: '{n} llythyren neu fwy',
      many: '{n} llythyren neu fwy',
      other: '{n} llythyren neu fwy',
    },
    seconds: {
      zero: '{n} eiliad',
      one: '{n} eiliad',
      two: '{n} eiliad',
      few: '{n} eiliad',
      many: '{n} eiliad',
      other: '{n} eiliad',
    },
  },
}
